import { DOC_TYPE } from '@allocab/allocab-commons-shared-types';
import { DocumentItemOutput } from '@allocab/allocab-driver-service-types';
import {
  FileCircleCheckSolid,
  FileCircleClockSolid,
  FileCircleExclamationSolid,
  FileCircleXmarkSolid,
} from '@allocab/allocab-icons';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { DocumentItemModal } from './DocumentItemModal';
import { t } from '../../services/translation/translation';
import { getDocumentItemStatus } from '../../utils/document';
import { Spinner } from '../Spinner';

type Props = {
  isLoading: boolean;
  documentItems?: DocumentItemOutput[];
  vehicleId?: string;
  driverKey?: string;
  compressed?: boolean;
};

export const DocumentItemList = ({ isLoading, documentItems, vehicleId, driverKey, compressed }: Props) => {
  const [documentItemType, setDocumentItemType] = useState<DOC_TYPE | null>(null);
  const selectedDocumentItem = documentItems?.find(d => d.type === documentItemType);
  const nbInvalidDocuments =
    documentItems?.filter(d => ['REJECTED', 'TO_DEFINE'].includes(getDocumentItemStatus(d))).length ?? 0;

  return (
    <div
      className={twMerge(
        'flex-col min-w-full sm:min-w-0 gap-2 p-6 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700',
        compressed ? 'flex' : 'inline-flex',
      )}
    >
      {!compressed ? (
        <div className="flex items-center gap-2">
          <h6 className="text-lg font-bold dark:text-white">{t('common.documents')}</h6>
          {nbInvalidDocuments > 0 ? <span className="flex w-2 h-2 bg-red-500 rounded-full " /> : null}
        </div>
      ) : null}
      {isLoading ? <Spinner /> : null}
      <ul className="text-sm cursor-pointer text-gray-700 dark:text-gray-200">
        {documentItems?.map(item => {
          const status = getDocumentItemStatus(item);
          return (
            <li key={item.type}>
              <button
                type="button"
                className="w-full flex items-center px-2 py-2 gap-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                onClick={() => setDocumentItemType(item.type as DOC_TYPE)}
              >
                {status === 'VALIDATED' ? (
                  <FileCircleCheckSolid width={20} height={20} className="fill-green-600" />
                ) : status === 'REJECTED' ? (
                  <FileCircleXmarkSolid width={20} height={20} className="fill-red-600" />
                ) : status === 'MODIFIED' ? (
                  <FileCircleClockSolid width={20} height={20} className="fill-yellow-600" />
                ) : (
                  <FileCircleExclamationSolid width={20} height={20} className="fill-red-600" />
                )}
                <span className="text-start">{item.title}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {selectedDocumentItem ? (
        <DocumentItemModal
          documentItem={selectedDocumentItem}
          onClose={() => setDocumentItemType(null)}
          vehicleId={vehicleId}
          driverKey={driverKey}
        />
      ) : null}
    </div>
  );
};
